import {inject, Injectable, signal} from '@angular/core';
import {ProjectRoleType} from '@frontend/shared-api';
import {TranslatedOption} from '../../../../../shared/services/enum-translation-mapper.service';
import {finalize, Observable, Subject} from 'rxjs';
import {ToastService} from '@frontend/toast';
import {ProjectUserService} from '../project-user.service';

@Injectable({
  providedIn: 'root',
})
export class UserInvitationTemplateDialogService {
  private readonly projectUserService = inject(ProjectUserService);
  private readonly toast = inject(ToastService);

  readonly loading = signal<boolean>(false);
  readonly visible = signal<boolean>(false);
  readonly selectedRole = signal<TranslatedOption<ProjectRoleType> | null>(null);
  readonly templateContent = signal<string>('');
  readonly isSavedTemplate = signal<boolean>(false);
  readonly templateId = signal<number | null>(null);

  private readonly templateSavedSubject = new Subject<void>();

  readonly templateSaved$: Observable<void> = this.templateSavedSubject.asObservable();

  openDialog(userRole: TranslatedOption<ProjectRoleType>): void {
    this.loading.set(true);
    this.selectedRole.set(userRole);
    this.templateId.set(null);

    const savedCustomTemplate = this.projectUserService.findCustomTemplateByRole(userRole.enum);

    if (savedCustomTemplate) {
      this.isSavedTemplate.set(true);
      this.templateId.set(savedCustomTemplate.id);
      this.templateContent.set(savedCustomTemplate.content ?? '');
    } else {
      const content = this.projectUserService.getTemplateContentForRole(userRole.enum);
      this.isSavedTemplate.set(false);
      this.templateContent.set(content);
    }

    this.loading.set(false);
    this.visible.set(true);
  }

  closeDialog(): void {
    this.visible.set(false);
  }

  saveTemplateContent(projectId: number, content: string): void {
    const selected = this.selectedRole();
    if (selected == null) return;

    const isExisting = this.isSavedTemplate();
    const id = this.templateId();
    if (isExisting && id == null) return;

    const role = selected.enum;
    this.loading.set(true);

    this.projectUserService
      .saveTemplateContent(projectId, role, content, isExisting ? id : undefined)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: () => {
          this.toast.showSuccess(
            'userInvitationTemplate.save.success',
            'userInvitationTemplate.save.success.detail',
          );
          this.templateSavedSubject.next();
          this.closeDialog();
        },
      });
  }
}
